import { useState, useCallback, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import type { Run, RerunMode, UrlResult } from "../../lib/types.js";
import { CheckRow } from "../../components/CheckRow/CheckRow.js";
import { RerunBar } from "../../components/RerunBar/RerunBar.js";
import { LiveStream } from "../../components/LiveStream/LiveStream.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import styles from "./RunDetail.module.css";

function formatUrlPath(url: string, urlType: string): string {
  // Synthetic placeholder URLs (no feed URL configured)
  if (url.startsWith("(no ")) return url;
  // For file-based types show just the filename/path
  if (urlType === "robots" || urlType === "adstxt" || urlType === "feed" || urlType === "sitemap" || urlType === "news_sitemap") {
    try {
      const { pathname } = new URL(url);
      return pathname;
    } catch {
      return url.slice(0, 40);
    }
  }
  try {
    const { pathname, search } = new URL(url);
    const full = pathname + search;
    // Short URLs (portada / categorias): show full path
    if (full.length <= 24) return full;
    // Articles / AMP: show last two path segments to keep it meaningful
    const segments = pathname.replace(/\/$/, "").split("/").filter(Boolean);
    if (segments.length >= 2) {
      const slug = segments[segments.length - 1];
      // Truncate very long slugs
      const display = slug.length > 32 ? slug.slice(0, 30) + "…" : slug;
      return `/${segments[segments.length - 2]}/${display}${search}`;
    }
    return full.slice(0, 32) + (full.length > 32 ? "…" : "");
  } catch {
    return url.slice(0, 32);
  }
}

export function RunDetailPage() {
  const { runId = "" } = useParams<{ runId: string }>();
  const queryClient = useQueryClient();
  const [selectedUrlIndex, setSelectedUrlIndex] = useState(0);
  const [selectedSlugs, setSelectedSlugs] = useState(new Set<string>());
  const [liveRunId, setLiveRunId] = useState<string | null>(null);

  const { data: run, isLoading } = useQuery({
    queryKey: ["run", runId],
    queryFn: async () => {
      const { data } = await api.get<Run>(`/runs/${runId}`);
      return data;
    },
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "running" || status === "queued" ? 3000 : false;
    },
  });

  // Auto-activate live stream when navigating to an already-running run
  useEffect(() => {
    if (run?.status === "running" || run?.status === "queued") {
      setLiveRunId(run.id);
    } else {
      setLiveRunId(null);
    }
  }, [run?.status, run?.id]);

  const rerunMutation = useMutation({
    mutationFn: async ({ mode, slug, url }: { mode: RerunMode; slug?: string; url?: string }) => {
      const { data } = await api.post<{ runId: string }>(`/projects/${run?.projectId}/runs/${runId}/rerun`, {
        mode,
        slug,
        url,
      });
      return data;
    },
    onSuccess: ({ runId: newRunId }) => {
      setLiveRunId(newRunId);
      void queryClient.invalidateQueries({ queryKey: ["run", runId] });
    },
  });

  const handleRerun = useCallback(
    (mode: RerunMode, slug?: string, url?: string) => {
      rerunMutation.mutate({ mode, slug, url });
    },
    [rerunMutation],
  );

  if (isLoading) return <Skeleton height="400px" borderRadius="8px" />;
  if (!run) return <div className={styles.error}>Run not found</div>;

  const urlResults = run.urlResults ?? [];
  const currentUrl: UrlResult | undefined = urlResults[selectedUrlIndex];
  const checks = currentUrl?.checkResults ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link to={`/projects/${run.projectId}`} className={styles.back}>
          ← Back to project
        </Link>
        <div className={styles.meta}>
          <h1 className={styles.runId}>Run #{run.id.slice(0, 8)}</h1>
          <span className={`${styles.statusBadge} ${styles[run.status]}`}>
            {run.status}
          </span>
          {run.summary && (
            <span className={styles.score}>{run.summary.score}%</span>
          )}
          <span className={styles.time}>
            {new Date(run.startedAt).toLocaleString()}
          </span>
        </div>
        {run.parentRunId && (
          <Link
            to={`/runs/${run.parentRunId}`}
            className={styles.parentLink}
          >
            ↩ Parent: #{run.parentRunId.slice(0, 8)}
          </Link>
        )}
      </div>

      {(run.status === "done" || run.status === "failed") && (
        <RerunBar
          run={run}
          selectedSlugs={selectedSlugs}
          onRerun={handleRerun}
          isRunning={rerunMutation.isPending}
        />
      )}

      {run.status === "failed" && (run.summary as { error?: string } | null)?.error && (
        <div className={styles.errorBanner}>
          <strong>Error:</strong>{" "}
          {(run.summary as { error: string }).error}
        </div>
      )}

      {liveRunId && (
        <LiveStream
          runId={liveRunId}
          onComplete={() => {
            setLiveRunId(null);
            void queryClient.invalidateQueries({ queryKey: ["run", runId] });
          }}
        />
      )}

      {urlResults.length > 0 && (
        <div className={styles.urlLayout}>
          <div className={styles.urlSidebar}>
            {urlResults.map((ur, i) => {
              const passCount = ur.checkResults.filter((c) => c.ok).length;
              const failCount = ur.checkResults.filter((c) => !c.ok).length;
              return (
                <button
                  key={ur.id}
                  className={`${styles.urlTab} ${i === selectedUrlIndex ? styles.activeUrlTab : ""}`}
                  onClick={() => setSelectedUrlIndex(i)}
                  title={ur.url}
                >
                  <span className={styles.urlType}>{ur.url_type}</span>
                  <span className={styles.urlPath}>
                    {formatUrlPath(ur.url, ur.url_type ?? "")}
                  </span>
                  <span className={styles.urlCounts}>
                    <span className={styles.pass}>{passCount}</span>
                    <span className={styles.sep}>/</span>
                    <span className={styles.fail}>{failCount}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className={styles.checkList}>
            {checks.length === 0 ? (
              <div className={styles.empty}>
                {run.status === "running" || run.status === "queued"
                  ? "⏳ Run in progress — results will appear when complete."
                  : "No check results for this URL."}
              </div>
            ) : (
              checks.map((c) => (
                <CheckRow
                  key={c.slug}
                  check={c}
                  selected={selectedSlugs.has(c.slug)}
                  onSelect={(sel) => {
                    setSelectedSlugs((prev) => {
                      const next = new Set(prev);
                  if (sel) next.add(c.slug);
                  else next.delete(c.slug);
                  return next;
                });
              }}
              onRerun={
                  !c.ok && currentUrl
                    ? () => handleRerun("check-url", c.slug, currentUrl.url)
                    : undefined
                }
                disabled={rerunMutation.isPending}
              />
            ))
          )}
          </div>
        </div>
      )}
    </div>
  );
}
